import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { List, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ErrorBoundary, useToast } from '@/components/common';
import { useSessionStore } from '@/stores/useSessionStore';
import { useSessionListStore } from '@/stores/useSessionListStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFirmContextStore } from '@/stores/useFirmContextStore';
import { useCreateSession, useSessionStatus } from '@/hooks/useSession';
import {
  UploadDropzone,
  FilePreview,
  QuestionExtractor,
  ProcessingScreen,
  UploadStepper,
  UploadDetailsAccordion,
  useFileParser,
  useEstimate,
  validateFile,
  type ValidationStatus,
} from '@/features/upload';
import type { FileRejection } from 'react-dropzone';
import type {
  CreateSessionRequestWithTable,
  QuestionItem,
  SessionPriority,
} from '@/types/api.types';

/**
 * Page stages representing the upload workflow:
 * - idle: Initial state, waiting for file upload
 * - file-selected: File uploaded and parsed, showing question preview with inline estimate
 * - processing: Session created, polling for completion
 * - error: An error occurred
 */
type PageStage = 'idle' | 'file-selected' | 'processing' | 'error';

/**
 * Upload page wrapped with error boundary for graceful error handling.
 */
export function UploadPage() {
  const handleError = useCallback((error: Error) => {
    console.error('[UploadPage] Error caught by boundary:', error);
  }, []);

  return (
    <ErrorBoundary onError={handleError}>
      <UploadPageContent />
    </ErrorBoundary>
  );
}

/**
 * Internal upload page content component.
 */
function UploadPageContent() {
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const { firmId, setFirmId, productId, setProductId, reset: resetStore } = useSessionStore();
  const { user } = useAuthStore();
  const activeFirmId = useFirmContextStore((state) => state.activeFirmId);

  // File state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [pageStage, setPageStage] = useState<PageStage>('idle');

  // Session metadata state
  const [sourceConsultant, setSourceConsultant] = useState('');
  const [accountName, setAccountName] = useState('');
  const [priority, setPriority] = useState<SessionPriority>('normal');

  // Session state for processing
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [showManualLink, setShowManualLink] = useState(false);

  // Hooks
  const {
    questions,
    parseFile,
    reset: resetParser,
    status: parserStatus,
    error: parserError,
    anchorsById,
    tableShapesById,
    answerTargetsById,
    originalFileRef,
  } = useFileParser();
  const createSession = useCreateSession();
  const addSession = useSessionListStore((state) => state.addSession);
  const { estimate, runEstimate, isPending: isEstimating, reset: resetEstimate } = useEstimate();

  useEffect(() => {
    if (firmId === null && activeFirmId !== null) {
      setFirmId(activeFirmId);
    }
  }, [activeFirmId, firmId, setFirmId]);

  // Status polling
  const {
    progress,
    isPolling,
    isReady,
    isFailed,
  } = useSessionStatus(activeSessionId, {
    enabled: !!activeSessionId,
    onReady: () => {},
    onError: (error) => {
      setFileError(error.message);
      showError('Processing Error', error.message);
    },
  });

  // Auto-redirect to review when processing is complete
  useEffect(() => {
    if (isReady && activeSessionId) {
      const timer = setTimeout(() => {
        navigate(`/sessions/${activeSessionId}/review`);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isReady, activeSessionId, navigate]);

  // Show manual "Go to review" link after 30s as fallback if polling stalls.
  // The render guard below hides stale state once processing finishes/fails.
  useEffect(() => {
    if (!activeSessionId || isReady || isFailed) return;

    const timer = setTimeout(() => setShowManualLink(true), 30_000);
    return () => clearTimeout(timer);
  }, [activeSessionId, isReady, isFailed]);

  const shouldShowManualLink = showManualLink && !!activeSessionId && !isReady && !isFailed;
  const requiresFirmSelection = user?.isSuperAdmin === true && firmId == null;

  const handleFilesAccepted = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;

      const validation = validateFile(file);
      if (!validation.isValid) {
        setFileError(validation.error || 'Invalid file');
        setSelectedFile(null);
        return;
      }

      setFileError(null);
      setSelectedFile(file);

      const result = await parseFile(file);
      if (result.success) {
        setPageStage('file-selected');
      } else {
        setFileError(result.error || 'Failed to parse file');
        setPageStage('error');
      }
    },
    [parseFile]
  );

  const handleFilesRejected = useCallback((rejections: FileRejection[]) => {
    const rejection = rejections[0];
    if (rejection) {
      const errorMessage = rejection.errors.map((e) => e.message).join(', ');
      setFileError(errorMessage);
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setFileError(null);
    setPageStage('idle');
    resetParser();
    resetEstimate();
    setActiveSessionId(null);
  }, [resetParser, resetEstimate]);

  // When questions are confirmed from the extractor, run estimate then start processing
  const handleConfirmQuestions = useCallback(
    (questionsToProcess: QuestionItem[]) => {
      if (!selectedFile || questionsToProcess.length === 0) return;
      if (requiresFirmSelection) return;

      // Run estimate in the background for display, but go straight to processing
      runEstimate({
        firm_id: firmId ?? undefined,
        product_id: productId,
        questions: questionsToProcess.map((q) => q.question),
      });

      // Start processing immediately
      setPageStage('processing');
      setFileError(null);

      // Typed via the extended request so the additive table fields pass through
      // the `CreateSessionRequest`-typed mutation without excess-property errors.
      const sessionPayload: CreateSessionRequestWithTable = {
        firm_id: firmId ?? undefined,
        product_id: productId,
        uploaded_filename: selectedFile.name,
        questions: questionsToProcess.map((q) => q.question),
        source_consultant: sourceConsultant || undefined,
        account_name: accountName || undefined,
        priority,
        // M2 (F5): anchors stay aligned with the (possibly edited) question
        // list by question_id lookup; only present for server-extracted files.
        anchors: originalFileRef
          ? questionsToProcess.map((q) => anchorsById[q.question_id] ?? null)
          : undefined,
        // Wellington #1: table metadata stays aligned the same way, driving
        // deterministic table-cell prefill on the backend.
        table_shapes: originalFileRef
          ? questionsToProcess.map((q) => tableShapesById[q.question_id] ?? null)
          : undefined,
        answer_targets: originalFileRef
          ? questionsToProcess.map((q) => answerTargetsById[q.question_id] ?? null)
          : undefined,
        original_file_ref: originalFileRef ?? undefined,
      };

      createSession.mutate(
        sessionPayload,
        {
          onSuccess: (data) => {
            setActiveSessionId(data.session_id);
            // Optimistically add to the session list store so the new session
            // is visible on /sessions immediately ("View All" mid-processing),
            // before the list query refetches. The SessionsPage merge replaces
            // this entry with the API version on the next refetch.
            addSession({
              id: data.session_id,
              fileName: selectedFile.name,
              totalQuestions: questionsToProcess.length,
              processedQuestions: 0,
              exactMatches: 0,
              suggestedMatches: 0,
              generatedResponses: 0,
              noMatches: 0,
              automationRate: 0,
              status: 'Processing',
              created: new Date().toISOString(),
              priority,
              sourceConsultant: sourceConsultant || null,
              accountName: accountName || null,
            });
          },
          onError: (error) => {
            const errorMsg = error.message || 'Failed to create session';
            setFileError(errorMsg);
            setPageStage('error');
            showError('Session Creation Failed', errorMsg);
          },
        }
      );
    },
    [selectedFile, firmId, productId, sourceConsultant, accountName, priority, runEstimate, createSession, addSession, showError, anchorsById, tableShapesById, answerTargetsById, originalFileRef, requiresFirmSelection]
  );

  const handleCancelQuestionPreview = useCallback(() => {
    setPageStage('idle');
    handleRemoveFile();
  }, [handleRemoveFile]);

  const handleCancelProcessing = useCallback(() => {
    setActiveSessionId(null);
    setPageStage('idle');
    handleRemoveFile();
    resetStore();
  }, [handleRemoveFile, resetStore]);

  const handleRetryProcessing = useCallback(() => {
    if (selectedFile && questions.length > 0) {
      setActiveSessionId(null);
      setFileError(null);
      setPageStage('file-selected');
    } else {
      handleCancelProcessing();
    }
  }, [selectedFile, questions, handleCancelProcessing]);

  const handleViewResults = useCallback(() => {
    if (activeSessionId) {
      navigate(`/sessions/${activeSessionId}/review`);
    }
  }, [activeSessionId, navigate]);

  const getValidationStatus = (): ValidationStatus => {
    if (parserStatus === 'parsing') return 'validating';
    if (parserStatus === 'success') return 'valid';
    if (parserStatus === 'error') return 'invalid';
    return 'idle';
  };

  const isProcessing = pageStage === 'processing' || createSession.isPending;

  const questionItems: QuestionItem[] = questions.map((q) => ({
    question_id: q.question_id,
    question: q.question,
  }));

  // Render processing screen
  if (pageStage === 'processing' && activeSessionId) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <UploadStepper currentStep="processing" />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Processing Questionnaire</h1>
            <p className="text-sm text-muted-foreground">
              {selectedFile?.name || 'Your questionnaire is being processed'}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/sessions">
              <List className="h-4 w-4 mr-2" />
              View All
            </Link>
          </Button>
        </div>

        {/* Inline estimate summary while processing */}
        {estimate && !isEstimating && (
          <Card>
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-6 text-sm">
                <span>
                  <span className="font-medium">{estimate.question_count}</span>
                  <span className="text-muted-foreground"> questions</span>
                </span>
                <span>
                  <span className="font-medium text-primary">{estimate.automation_rate.toFixed(0)}%</span>
                  <span className="text-muted-foreground"> estimated auto-fill rate</span>
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        <ProcessingScreen
          sessionId={activeSessionId}
          progress={progress}
          isPolling={isPolling}
          isReady={isReady}
          isFailed={isFailed}
          onCancel={handleCancelProcessing}
          onRetry={handleRetryProcessing}
          onViewResults={handleViewResults}
        />

        {shouldShowManualLink && (
          <div className="text-center mt-4">
            <p className="text-sm text-muted-foreground mb-2">
              Taking longer than expected?
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/sessions/${activeSessionId}/review`)}
            >
              Go to review page
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Render question preview/extraction screen
  if (pageStage === 'file-selected' && questions.length > 0) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <UploadStepper currentStep="preview" />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Preview Questions</h1>
            <p className="text-sm text-muted-foreground">
              {selectedFile?.name} &mdash; {questions.length} questions found
            </p>
          </div>
          <Button variant="outline" onClick={handleCancelQuestionPreview}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Error message */}
        {fileError && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="text-sm">{fileError}</span>
          </div>
        )}

        {/* Question Extractor — button says "Process [N] questions" */}
        <QuestionExtractor
          questions={questionItems}
          onConfirm={handleConfirmQuestions}
          onCancel={handleCancelQuestionPreview}
          isProcessing={createSession.isPending}
          isConfirmDisabled={requiresFirmSelection}
          confirmDisabledMessage={
            requiresFirmSelection ? 'Select a firm to process this DDQ.' : undefined
          }
        />
      </div>
    );
  }

  // Default: Upload screen
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <UploadStepper currentStep="upload" />

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Upload a Questionnaire</h1>
          <p className="text-sm text-muted-foreground">
            We'll extract the questions and pre-fill answers from APX Vault and approved response history
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/sessions">
            <List className="h-4 w-4 mr-2" />
            View All
          </Link>
        </Button>
      </div>

      {/* Optional Details (collapsed by default) */}
      <UploadDetailsAccordion
        sourceConsultant={sourceConsultant}
        accountName={accountName}
        priority={priority}
        firmId={firmId}
        productId={productId}
        onSourceConsultantChange={setSourceConsultant}
        onAccountNameChange={setAccountName}
        onPriorityChange={setPriority}
        onFirmChange={setFirmId}
        onProductChange={setProductId}
        disabled={isProcessing}
      />

      {/* Upload Zone */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          {/* File Error */}
          {fileError && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="text-sm">{fileError}</span>
            </div>
          )}

          {/* Dropzone or File Preview */}
          {!selectedFile ? (
            <UploadDropzone
              onFilesAccepted={handleFilesAccepted}
              onFilesRejected={handleFilesRejected}
              disabled={isProcessing}
            />
          ) : (
            <FilePreview
              file={selectedFile}
              onRemove={handleRemoveFile}
              validationStatus={getValidationStatus()}
              validationError={parserError || undefined}
              questionsCount={questions.length}
              disabled={isProcessing}
            />
          )}

          {/* Continue Button */}
          {selectedFile && !isProcessing && parserStatus === 'success' && (
            <div className="flex justify-center pt-2">
              <Button
                onClick={() => setPageStage('file-selected')}
                size="lg"
                disabled={questions.length === 0}
              >
                Preview Questions ({questions.length})
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
